/**
 * B2C Frontend Configuration
 *
 * Configuration priority (highest to lowest):
 * 1. window.__COMPANY_CONFIG__ - Injected by CloudFront/Lambda@Edge in production
 * 2. Environment variables - For local development (.env file)
 * 3. URL slug extraction - Parse company slug from URL and look up via API
 * 4. Default values - Fallback for development
 */

const {
  REACT_APP_SERVER_URL,
  REACT_APP_API_ENDPOINT,
  REACT_APP_COMPANY_ID,
  REACT_APP_COMPANY_SLUG,
} = process.env

/**
 * Extract company slug from current URL
 * Supports both path-based and subdomain patterns
 * Returns { slug, isPathBased } to determine router basename
 */
function extractSlugFromUrl() {
  const hostname = window.location.hostname
  const pathname = window.location.pathname

  // Subdomain pattern: {slug}.book.mygroupbooker.com
  const subdomainMatch = hostname.match(/^([a-z0-9-]+)\.book\.mygroupbooker\.com$/i)
  if (subdomainMatch) {
    return { slug: subdomainMatch[1].toLowerCase(), isPathBased: false }
  }

  // Path-based pattern: book.mygroupbooker.com/{slug}
  if (hostname.match(/^book\.mygroupbooker\.com$/i)) {
    const pathMatch = pathname.match(/^\/([a-z0-9-]+)(\/.*)?$/i)
    if (pathMatch) {
      return { slug: pathMatch[1].toLowerCase(), isPathBased: true }
    }
  }

  return null
}

/**
 * Check if we're on the production book domain (book.mygroupbooker.com)
 * but without a valid company slug in the URL
 */
function isBookDomainWithoutSlug() {
  const hostname = window.location.hostname
  return /^book\.mygroupbooker\.com$/i.test(hostname) && !extractSlugFromUrl()
}

/**
 * Check for preview mode from URL query params
 */
function getPreviewToken() {
  const params = new URLSearchParams(window.location.search)
  return params.get('preview_token')
}

/**
 * Get company configuration from injected values or environment
 */
function getCompanyConfig() {
  // Priority 1: Injected by CloudFront/Lambda@Edge
  if (typeof window !== 'undefined' && window.__COMPANY_CONFIG__) {
    return {
      companyId: window.__COMPANY_CONFIG__.companyId,
      companySlug: window.__COMPANY_CONFIG__.companySlug,
      companyName: window.__COMPANY_CONFIG__.companyName,
      isPreviewMode: window.__COMPANY_CONFIG__.isPreviewMode || false,
      source: 'injected',
    }
  }

  // Priority 2: Environment variables (local development)
  if (REACT_APP_COMPANY_ID) {
    return {
      companyId: REACT_APP_COMPANY_ID,
      companySlug: REACT_APP_COMPANY_SLUG || null,
      companyName: null,
      isPreviewMode: false,
      source: 'env',
    }
  }

  // Priority 3: Extract from URL (will need API lookup)
  const slugInfo = extractSlugFromUrl()
  if (slugInfo) {
    return {
      companyId: null, // Will be resolved via API
      companySlug: slugInfo.slug,
      companyName: null,
      isPreviewMode: !!getPreviewToken(),
      previewToken: getPreviewToken(),
      isPathBased: slugInfo.isPathBased,
      source: 'url',
    }
  }

  // Priority 4: Default for local development
  return {
    companyId: '1',
    companySlug: null,
    companyName: null,
    isPreviewMode: false,
    source: 'default',
  }
}

const companyConfig = getCompanyConfig()

const config = {
  api: {
    endpoint: REACT_APP_API_ENDPOINT || 'http://localhost:8000/api/v1',
  },
  server: REACT_APP_SERVER_URL || 'http://localhost:8000',

  // Company configuration
  companyId: companyConfig.companyId,
  companySlug: companyConfig.companySlug,
  companyName: companyConfig.companyName,
  isPreviewMode: companyConfig.isPreviewMode,
  previewToken: companyConfig.previewToken || null,
  configSource: companyConfig.source,

  // Helper to check if we need to resolve company by slug
  needsSlugResolution: companyConfig.source === 'url' && !companyConfig.companyId,

  // Path-based URL indicator (for router basename)
  isPathBased: companyConfig.isPathBased || false,

  // True when on the production book domain without a valid slug
  noValidSlug: isBookDomainWithoutSlug(),
}

/**
 * Get the router basename for React Router
 * For path-based URLs (book.mygroupbooker.com/{slug}), returns /{slug}
 * For subdomain URLs ({slug}.book.mygroupbooker.com), returns empty string
 */
export function getRouterBasename() {
  if (config.isPathBased && config.companySlug) {
    return `/${config.companySlug}`
  }
  return ''
}

export default config
